/**
 * @file routes/_app/analytics/cases.tsx
 * @description 案件分析頁。
 *
 * 提供案件趨勢、狀態分布和 SLA 達成率分析。
 * 支援日期範圍篩選和分組方式切換（月/週/方案）。
 *
 * 視覺：PageShell + SectionCard + EmptyState（recharts AreaChart + BarChart）
 */

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { Button, DatePicker, Radio, Space, Spin } from 'antd';
import { message } from '@/lib/antd-app';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { FileDown, RefreshCw } from 'lucide-react';
import { type Dayjs } from 'dayjs';
import { useCaseTrend } from '@/hooks/useAnalytics';
import type { CaseGroupBy } from '@/lib/analytics-types';
import { PageShell, SectionCard, EmptyState } from '@/components/shell';
import { color, space } from '@/styles/tokens';

const { RangePicker } = DatePicker;

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/analytics/cases')({ component: AnalyticsCasesPage });

/** 案件分析頁 */
function AnalyticsCasesPage() {
  /* ── 篩選狀態 ── */
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [groupBy, setGroupBy] = useState<CaseGroupBy>('month');
  const [exporting, setExporting] = useState(false);

  const range = {
    from: dateRange?.[0]?.format('YYYY-MM-DD'),
    to: dateRange?.[1]?.format('YYYY-MM-DD'),
  };

  /* ── 資料查詢 ── */
  const trend = useCaseTrend(range, groupBy);
  const trendData = trend.data?.trend ?? [];

  /* ── 匯出 ── */
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ type: 'cases' });
      if (range.from) params.set('from', range.from);
      if (range.to) params.set('to', range.to);
      const resp = await fetch(`/api/analytics/export?${params.toString()}`, {
        credentials: 'same-origin',
      });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zhcb-cases-analytics-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('匯出成功');
    } catch (err) {
      message.error('匯出失敗');
      console.error('export cases analytics failed', err);
    } finally {
      setExporting(false);
    }
  }, [range]);

  return (
    <PageShell
      title="案件分析"
      description="案件新建趨勢、結案分析"
      actions={
        <Space>
          <Button
            icon={<RefreshCw size={14} />}
            onClick={() => void trend.refetch()}
            loading={trend.isFetching}
          >
            刷新
          </Button>
          <Button icon={<FileDown size={14} />} loading={exporting} onClick={handleExport}>
            匯出 Excel
          </Button>
        </Space>
      }
    >
      {/* 篩選列 */}
      <div style={{ marginBottom: space[4] }}>
        <SectionCard title="篩選條件">
          <Space wrap>
            <RangePicker
              value={dateRange}
              onChange={(v) => setDateRange(v)}
              placeholder={['開始日期', '結束日期']}
              format="YYYY-MM-DD"
            />
            <Radio.Group
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as CaseGroupBy)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="month">月</Radio.Button>
              <Radio.Button value="week">週</Radio.Button>
              <Radio.Button value="scheme">方案</Radio.Button>
            </Radio.Group>
          </Space>
        </SectionCard>
      </div>

      {/* 面積趨勢圖 */}
      <div style={{ marginBottom: space[4] }}>
        <SectionCard title="案件趨勢">
          {trend.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: space[8] }}>
              <Spin />
            </div>
          ) : trendData.length === 0 ? (
            <EmptyState title="暫無趨勢資料" size="sm" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trendData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <defs>
                  <linearGradient id="gradCreated" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color.brand.primary} stopOpacity={0.18} />
                    <stop offset="95%" stopColor={color.brand.primary} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradClosed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color.state.success} stopOpacity={0.18} />
                    <stop offset="95%" stopColor={color.state.success} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <RechartTooltip />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="created"
                  name="新建案件"
                  stroke={color.brand.primary}
                  fill="url(#gradCreated)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Area
                  type="monotone"
                  dataKey="closed"
                  name="結案"
                  stroke={color.state.success}
                  fill="url(#gradClosed)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </SectionCard>
      </div>

      {/* 柱狀比較圖 */}
      <SectionCard title="新建 vs 結案 比較">
        {trend.isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: space[8] }}>
            <Spin />
          </div>
        ) : trendData.length === 0 ? (
          <EmptyState title="暫無資料" size="sm" />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={trendData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <RechartTooltip />
              <Legend />
              <Bar
                dataKey="created"
                name="新建案件"
                fill={color.brand.primary}
                radius={[3, 3, 0, 0]}
              />
              <Bar dataKey="closed" name="結案" fill={color.state.success} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </SectionCard>
    </PageShell>
  );
}
